export type ProjectField = { key: string; label: string; required: boolean; type: 'text' | 'date' | 'number' | 'textarea' | 'select'; options?: string[] };

function fields(definition: string): ProjectField[] {
    return definition.trim().split('\n').map(line => {
        const [key, rawLabel, type = 'text', options = ''] = line.split('|');
        return { key, label: rawLabel.replace(/\*$/, ''), required: rawLabel.endsWith('*'), type: type as ProjectField['type'],
            ...(options ? { options: options.split(';') } : {}) };
    });
}

export const EP_PROJECT_PHASES = [
    { id: 'initiation', title: 'Project Initiation', fields: fields(`
projectName|Project reference name*|text
classification|Classification*|select|Confidential;Restricted;Internal
principalName|Principal full name*|text
principalRole|Principal title and organisation*|text
startDate|Project start date*|date
endDate|Project end date*|date
operationalLocations|Operational countries and cities*|textarea
projectType|Project type*|select|Multi-venue tour;Single event;Ongoing residential;Travel escort;Other
venueCount|Number of venues*|number
movementLegCount|Number of movement legs*|number
clientSecurityContact|Client security contact*|textarea
budgetUsd|Project budget, USD|number`) },
    { id: 'threat', title: 'Principal and Threat Assessment', fields: fields(`
principalThreatLevel|Principal threat level*|select|Critical;High;Medium;Low
knownThreatActors|Known threat actors, intent and capability*|textarea
priorIncidents|Prior security incidents*|textarea
elevatedThreatReason|Reason for elevated threat*|textarea
onlineMonitoring|Online threat monitoring, last 72 hours*|textarea
countryThreatLevels|Threat advisory per operational country*|textarea
foreignStateInterest|Foreign state interest*|textarea
kidnapRisk|Kidnap risk by country*|textarea
unrestRisk|Protest and civil unrest by country*|textarea
publicProfile|Principal public profile*|select|High;Medium;Low
biographicIntelNeeded|Pre-movement intelligence required*|select|Yes;No
principalMedicalRequirements|Principal medical requirements|textarea`) },
    { id: 'intelligence', title: 'Intelligence Integration', fields: [] as ProjectField[] },
    { id: 'travel', title: 'Travel and Itinerary Risk', fields: [] as ProjectField[] },
    { id: 'advance', title: 'Advance Work', fields: [] as ProjectField[] },
    { id: 'team', title: 'Team and Communications', fields: fields(`
detailLead|Detail lead or SAIC|text
agentCount|Total agents|number
closeProtectionCount|Close protection agents|number
advanceAgents|Advance agents|textarea
counterAssaultTeam|Counter-assault team and authority|textarea
vehicleFleet|Vehicle fleet|textarea
drivers|Drivers and qualifications|textarea
armouredVehicles|Armoured vehicles and rating|textarea
radioSystem|Primary radio system|text
encryptedMobile|Encrypted mobile platform|text
codeWords|Communications code words|textarea
commandPosts|Command post per venue|textarea`) },
    { id: 'medical', title: 'Medical and Contingency', fields: fields(`
medicalOfficers|Medical officers per country|textarea
medicalMobiles|Medical contact numbers|textarea
traumaCentres|Trauma centres per country|textarea
medevac|Air medical evacuation arrangements|textarea
ambulances|Ground ambulance per venue|textarea
principalBloodType|Principal blood type|text
principalMedications|Principal medications|textarea
casevacPrimary|Primary casevac routes per venue|textarea
casevacAlternate|Alternate casevac routes per venue|textarea
safeRoomBleedKits|Safe room bleed kits per venue|textarea
contingencyOne|Contingency scenario one|textarea
contingencyTwo|Contingency scenario two|textarea
contingencyThree|Contingency scenario three|textarea
crisisPlanReference|Crisis management plan reference|text`) },
    { id: 'generation', title: 'Operations Order and Briefing', fields: [] as ProjectField[] },
] as const;

export const EP_PROJECT_LIVE_PHASES = EP_PROJECT_PHASES.filter(phase => !['intelligence', 'travel', 'advance'].includes(phase.id));

export const EP_PROJECT_DOCUMENTS = [
    { key: 'operationsOrder', title: 'EP Operations Order' },
    { key: 'threatAssessment', title: 'Principal and Threat Assessment' },
    { key: 'intelAnnex', title: 'Intelligence Annex Package' },
    { key: 'advanceSummary', title: 'Advance Work Summary' },
    { key: 'teamBriefing', title: 'Team Briefing Pack' },
    { key: 'commsMedical', title: 'Communications and Medical Reference' },
    { key: 'aar', title: 'After Action Review' },
] as const;

export const EP_PROJECT_QA = [
    'All ten Operations Order sections reviewed',
    'Project reference and principal match throughout',
    'Threat ratings and named actors independently checked',
    'Current country and travel reports reviewed',
    'All operational countries have intelligence coverage',
    'All venue surveys complete and linked',
    'Four PACE routes reviewed per venue',
    'Safe rooms and liaison contacts verified',
    'Team assignments and drivers confirmed',
    'Radio channels and code words tested',
    'Trauma centres and casevac routes verified',
    'Conflicts across linked documents resolved',
    'All six release documents reviewed',
    'Client distribution and classification confirmed',
    'Invoice amount checked against Paddle price',
];

export const EP_PROJECT_LINK_TYPES = ['EP-11', 'S-12', 'S-05', 'S-06', 'S-07', 'TRM-01', 'S-08', 'S-09', 'S-13', 'S-01'] as const;

export function missingProjectFields(values: Record<string, unknown>, phaseId: string): string[] {
    const phase = EP_PROJECT_PHASES.find(item => item.id === phaseId);
    return phase ? phase.fields.filter(field => field.required && !String(values[field.key] ?? '').trim()).map(field => field.label) : [];
}

export function projectPhaseProgress(values: Record<string, unknown>, links: Array<{ type: string; status: string }>, venues: Array<{ ep11RequestId: string; status: string }>, hasDocuments: boolean) {
    const complete = (link: { status: string }) => ['delivered', 'invoiced', 'completed'].includes(link.status);
    return [
        !missingProjectFields(values, 'initiation').length && Number(values.venueCount) >= 1 && Number(values.venueCount) <= 12 &&
            Number.isInteger(Number(values.venueCount)) && Number(values.movementLegCount) >= 0 &&
            String(values.endDate || '') >= String(values.startDate || ''),
        !missingProjectFields(values, 'threat').length,
        links.some(link => ['S-05', 'S-06', 'S-07'].includes(link.type) && complete(link)),
        links.some(link => ['TRM-01', 'S-08', 'S-09', 'S-12'].includes(link.type) && complete(link)),
        venues.length === Number(values.venueCount) && venues.length <= 12 && venues.every(venue => venue.ep11RequestId && ['delivered', 'invoiced', 'completed'].includes(venue.status)),
        !missingProjectFields(values, 'team').length,
        !missingProjectFields(values, 'medical').length,
        hasDocuments,
    ];
}
